import React, {useState} from 'react';
import {
    Card, CardContent, CardActions, Typography, IconButton, TextField,
    Chip, Box, Tooltip, Link
} from '@mui/material';
import {
    Edit as EditIcon, Save as SaveIcon, Cancel as CancelIcon,
    Delete as DeleteIcon, Place as PlaceIcon, Language as LanguageIcon,
    OpenInNew as OpenInNewIcon
} from '@mui/icons-material';

const EndonymCard = ({
    addedLabel,
    onUpdate,
    onDelete,
    onMapFocus
}) => {
    const [isEditing, setIsEditing] = useState(false);
    const [editValue, setEditValue] = useState(addedLabel.label);

    const handleSave = () => {
        if (editValue.trim()) {
            onUpdate(addedLabel, editValue.trim());
            setIsEditing(false);
        }
    };

    const handleCancel = () => {
        setEditValue(addedLabel.label);
        setIsEditing(false);
    };

    const handleKeyPress = (event) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            handleSave();
        } else if (event.key === 'Escape') {
            handleCancel();
        }
    };

    const getLanguageDisplay = () => {
        if (addedLabel.language.customLabel) {
            return addedLabel.language.customLabel;
        }
        if (addedLabel.language.wikidataLabel) {
            return addedLabel.language.wikidataLabel;
        }
        return 'Unknown Language';
    };

    const getWikidataUrl = () => {
        if (!addedLabel.place.wikidataID) return null;
        const qId = addedLabel.place.wikidataID.replace('http://www.wikidata.org/entity/', '');
        return `https://www.wikidata.org/wiki/${qId}`;
    };


    return (
        <Card 
            elevation={2} 
            sx={{ 
                mb: 2, 
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': { 
                    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                    transform: 'translateY(-2px) scale(1.01)'
                }
            }}
        >
            <CardContent sx={{ pb: 1 }}>
                {/* Header with place name and actions */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Box sx={{ flex: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1 }}>
                            <PlaceIcon sx={{ color: 'primary.main' }} />
                            <Typography variant="h6" component="h3" sx={{ fontWeight: 'bold', flex: 1 }}>
                                {addedLabel.place.wikidataLabel || 'Unnamed Place'}
                            </Typography>
                            {getWikidataUrl() && (
                                <Tooltip title={`View on Wikidata`}>
                                    <IconButton
                                        component={Link}
                                        href={getWikidataUrl()}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        size="small"
                                        sx={{
                                            color: 'primary.main',
                                            '&:hover': {
                                                backgroundColor: 'primary.100'
                                            }
                                        }}
                                    >
                                        <OpenInNewIcon sx={{ fontSize: 16 }} />
                                    </IconButton>
                                </Tooltip>
                            )}
                        </Box>
                        
                        {/* Editable label */}
                        {isEditing ? (
                            <TextField
                                value={editValue}
                                onChange={(e) => setEditValue(e.target.value)}
                                onKeyDown={handleKeyPress}
                                fullWidth
                                size="small"
                                label="Endonym"
                                autoFocus
                                sx={{ mt: 1 }}
                            />
                        ) : (
                            <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                                <Typography 
                                    variant="h5" 
                                    sx={{ 
                                        color: 'primary.main', 
                                        fontWeight: 'bold',
                                        mr: 2
                                    }}
                                >
                                    "{addedLabel.label}"
                                </Typography>
                                <Chip 
                                    icon={<LanguageIcon />}
                                    label={getLanguageDisplay()}
                                    size="small"
                                    variant="outlined"
                                    sx={{ ml: 'auto' }}
                                />
                            </Box>
                        )}
                    </Box>
                </Box>
            </CardContent>

            <CardActions sx={{ justifyContent: 'flex-start', pt: 0 }}>
                {isEditing ? (
                    <Box>
                        <Tooltip title="Save changes">
                            <IconButton onClick={handleSave} color="primary" size="small">
                                <SaveIcon />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Cancel editing">
                            <IconButton onClick={handleCancel} color="secondary" size="small">
                                <CancelIcon />
                            </IconButton>
                        </Tooltip>
                    </Box>
                ) : (
                    <Box>
                        <Tooltip title="Edit endonym">
                            <IconButton 
                                onClick={() => setIsEditing(true)} 
                                color="primary" 
                                size="small"
                            >
                                <EditIcon />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Focus on map">
                            <IconButton 
                                onClick={() => onMapFocus(addedLabel.place)} 
                                color="info" 
                                size="small"
                            >
                                <PlaceIcon />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Remove endonym">
                            <IconButton 
                                onClick={() => onDelete(addedLabel)} 
                                color="error" 
                                size="small"
                            >
                                <DeleteIcon />
                            </IconButton>
                        </Tooltip>
                    </Box>
                )}
            </CardActions>
        </Card>
    );
};

export default EndonymCard;